// Library Methods
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import {
  ThemeProvider as LightThemeProvider,
  useTheme,
} from "@mui/material/styles";
import DefaultDrawer from "src/components/shared/drawers/DefaultDrawer/DefaultDrawer";
import Alert from "src/components/shared/alerts/Alert/Alert";
import SelectEyeRadio from "./SelectEyeRadio/SelectEyeRadio";
import SelectAnalysisCard from "./SelectAnalysisCard/SelectAnalysisCard";
import LineChartContainer from "./LineChartContainer/LineChartContainer";
import PatternChartContainer from "./PatternChartContainer/PatternChartContainer";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

// Types
import { PatientType } from "src/services/patients/types";
import {
  ProgressionExamSummaryType,
  SelectedProgressionExamType,
} from "src/services/exams/types";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Utils
import { getTruncatedText } from "src/utils/text/textHelper";
import { sortByOldestDate } from "src/utils/date/dateHelper";
import { setupTheme } from "src/utils/theme/themeHelper";

// Hooks
import { useProgressionExams } from "src/services/exams/hooks/useProgressionExams";
import { useReportSectionData } from "src/services/exams/hooks/report-sections/useReportSectionData";

// API
import { getExternalExamCharts } from "src/services/exams/api";
import { useAddProgressionAnalysis } from "src/services/exams/hooks/progression-analyses/useAddProgressionAnalysis";
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { isRetinaLogikExam } from "src/utils/exams/progressionAnalysisHelper";
import { formatProgressionAnalysisData } from "src/utils/exams/examReportsHelper";

// API
import { getExternalExamById } from "src/services/exams/api";

// Constants
import { MAX_EXAM_SELECTION, MIN_EXAM_SELECTION } from "src/constants/enums";

const DEFAULT_EYE_SELECTION = "Left";

type ProgressionAnalysisSliderType = {
  open: boolean;
  patient: PatientType;
  onClose: () => void;
};

const ProgressionAnalysisSlider = ({
  open,
  patient,
  onClose,
}: ProgressionAnalysisSliderType) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens();

  // This drawer's charts and cards aren't styled for dark mode, so it's
  // locked to light regardless of the app's theme -- same approach as the
  // report pages.
  const languageKey = i18n.language as keyof typeof priorityLanguageMap;
  const lightTheme = useMemo(
    () => setupTheme(theme.direction, languageKey, "light"),
    [theme.direction, languageKey]
  );

  const { clinicInfo } = useClinicContext();
  const { setSnackbarInfo } = useSnackbarContext();
  const { getToken } = useTokenAccess();

  const { mutate: addAnalysisMutation, isPending: isAddAnalysisPending } =
    useAddProgressionAnalysis(clinicInfo?.id);

  const { mutateAsync: reportSectionMutate, isPending: isFetchingExamSection } =
    useReportSectionData(clinicInfo?.id);

  const [selectedEye, setSelectedEye] = useState(DEFAULT_EYE_SELECTION);
  const [selectedExams, setSelectedExams] = useState<
    Array<SelectedProgressionExamType>
  >([]);
  const [isFetchingExternalExam, setIsFetchingExternalExam] = useState(false);

  const [lineChartData, setLineChartData] = useState<
    Array<{
      age: string | number;
      time: string;
      MD: string | number | null;
      PSD: string | number | null;
      VRFI: number | null;
      pointShape: string;
      isRetinaLogikExam: boolean;
    }>
  >([]);

  const selectedRetinaLogikExams = useMemo(
    () => selectedExams.filter(isRetinaLogikExam),
    [selectedExams]
  );

  const selectedExternalExams = useMemo(
    () => selectedExams.filter((exam) => !isRetinaLogikExam(exam)),
    [selectedExams]
  );

  const onChangeEye = useCallback((eyeValue: string) => {
    setSelectedExams([]);
    setLineChartData([]);
    setSelectedEye(eyeValue);
  }, []);

  // Reset data on the drawer back to the default eye (Left)
  const resetData = useCallback(() => {
    onChangeEye(DEFAULT_EYE_SELECTION);
  }, [onChangeEye]);

  // The backend already filters to eligible exams, merges both sources and
  // sorts them, so changing eye is just a refetch.
  const {
    data: sortedExams,
    isLoading: isLoadingExams,
    isFetching: isFetchingExams,
    isRefetching: isRefetchingExams,
  } = useProgressionExams(clinicInfo?.id, patient.id, selectedEye);

  const isLoading = useMemo(
    () =>
      isLoadingExams ||
      isFetchingExams ||
      isRefetchingExams ||
      isAddAnalysisPending,
    [isLoadingExams, isFetchingExams, isRefetchingExams, isAddAnalysisPending]
  );

  const fetchExternalExamDetail = useCallback(
    async (externalExamId: string) => {
      if (!clinicInfo?.id) return null;

      setIsFetchingExternalExam(true);
      try {
        const token = await getToken();
        const controller = new AbortController();
        return await getExternalExamById(
          token,
          clinicInfo.id,
          patient.id,
          externalExamId,
          controller.signal
        );
      } catch {
        setSnackbarInfo({
          variant: "error",
          message: t("Failed to load the external exam data."),
        });
        return null;
      } finally {
        setIsFetchingExternalExam(false);
      }
    },
    [clinicInfo?.id, patient.id, getToken, setSnackbarInfo, t]
  );

  const updateLineCharts = useCallback(
    (selectedExams: Array<SelectedProgressionExamType>) => {
      const newChart = formatProgressionAnalysisData(
        selectedExams,
        patient?.dateOfBirth,
        clinicInfo?.timezone
      );
      setLineChartData([...newChart]);
    },
    [patient?.dateOfBirth, clinicInfo?.timezone]
  );

  const applySelection = useCallback(
    (updatedSelection: Array<SelectedProgressionExamType>) => {
      const sorted = sortByOldestDate(
        updatedSelection,
        "completionDate"
      ) as Array<SelectedProgressionExamType>;
      setSelectedExams(sorted);
      updateLineCharts(sorted);
    },
    [updateLineCharts]
  );

  const deselectExam = useCallback(
    (exam: ProgressionExamSummaryType) => {
      applySelection(selectedExams.filter((e) => e.id !== exam.id));
    },
    [selectedExams, applySelection]
  );

  // Measurements are not in the list payload — they are fetched by id here, once
  // the clinician actually picks a card.
  const handleExamSelect = useCallback(
    async (exam: ProgressionExamSummaryType) => {
      if (selectedExams.some((e) => e.id === exam.id)) {
        deselectExam(exam);
        return;
      }

      const reportData = await reportSectionMutate({
        patientId: exam.patientId,
        examId: exam.examId as string,
        sectionId: exam.id,
      });

      if (!reportData) return;

      const parsedReportData = JSON.parse(reportData);

      applySelection([
        ...selectedExams,
        { ...exam, reportData: parsedReportData },
      ]);
    },
    [selectedExams, deselectExam, applySelection, reportSectionMutate]
  );

  const handleExternalExamSelect = useCallback(
    async (exam: ProgressionExamSummaryType) => {
      if (selectedExams.some((e) => e.id === exam.id)) {
        deselectExam(exam);
        return;
      }

      const detail = await fetchExternalExamDetail(exam.id);
      if (!detail) return;

      const token = await getToken();
      const controller = new AbortController();
      const charts = await getExternalExamCharts(
        token,
        clinicInfo?.id as string,
        exam.patientId,
        exam.id,
        controller.signal
      );

      applySelection([
        ...selectedExams,
        { ...exam, data: detail.data, charts },
      ]);
    },
    [
      selectedExams,
      deselectExam,
      applySelection,
      fetchExternalExamDetail,
      getToken,
      clinicInfo?.id,
    ]
  );

  const handleAddProgressAnalysis = useCallback(() => {
    if (selectedExams.length <= 2) {
      setSnackbarInfo({
        variant: "error",
        message: t(
          "At least 3 exams must be selected to create a progression analysis."
        ),
      });
      return;
    }

    const examIds = selectedExams
      .filter(isRetinaLogikExam)
      .map((exam) => exam.examId as string);
    const externalExamIds = selectedExams
      .filter((exam) => !isRetinaLogikExam(exam))
      .map((exam) => exam.id);
    const examData = { eye: selectedEye, examIds, externalExamIds };

    addAnalysisMutation(
      { patientId: patient.id, examData },
      {
        onSuccess: (response) => {
          // navigate to new reports page
          setTimeout(() => {
            onClose();
            const reportUrl = `/patients/${patient.id}/progression-analysis/${response.id}`;
            navigate(reportUrl);
          }, 500);
        },
      }
    );
  }, [
    t,
    navigate,
    onClose,
    setSnackbarInfo,
    addAnalysisMutation,
    patient.id,
    selectedExams,
    selectedEye,
  ]);

  return (
    <LightThemeProvider theme={lightTheme}>
      <DefaultDrawer
        title={`${t("Add Progression Analysis")} / ${getTruncatedText(`${patient.firstName} ${patient.lastName}`)} / ${patient?.dateOfBirth?.slice(0, 10)}`}
        isOpen={open}
        onClose={() => {
          resetData();
          onClose();
        }}
        showCloseButton={false}
        customStyles={{
          width: "100%",
          height: "100vh",
          position: "relative",
          backgroundColor: "#FFF",
          color: onSurface,
        }}
        extraHeaderButtons={[
          <Box
            sx={{
              display: "flex",
              gap: "16px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              sx={ModalCustomCancel}
              disabled={isLoading}
              onClick={() => {
                resetData();
                onClose();
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              sx={ModalCustomConfirm}
              disabled={isLoading || selectedExams?.length < MIN_EXAM_SELECTION}
              onClick={handleAddProgressAnalysis}
            >
              {t("Generate")}
            </Button>
          </Box>,
        ]}
      >
        {selectedExams.length >= MAX_EXAM_SELECTION && (
          <Alert
            variant="error"
            alertContent={t("You can select a maximum of {{ maxNum }} exams.", {
              maxNum: MAX_EXAM_SELECTION,
            })}
            props={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              padding: "12px",
              borderRadius: 0,
            }}
          />
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            overflowY: { xs: "auto", md: "hidden" },
          }}
        >
          <LineChartContainer lineChartData={lineChartData} />
          <PatternChartContainer
            selectedExternalExams={selectedExternalExams}
            selectedRetinaLogikExams={selectedRetinaLogikExams}
          />
        </Box>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            paddingTop: "16px",
            gap: "16px",
            borderTop: "1px solid #435B6D7A",
            position: "sticky",
            bottom: 0,
            backgroundColor: surface,
          }}
        >
          <Box sx={{ paddingLeft: "20px" }}>
            <SelectEyeRadio
              selectedEye={selectedEye}
              onChangeEye={onChangeEye}
            />
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              paddingBottom: "16px",
              px: "20px",
              gap: "20px",
              overflowX: "auto",
              borderBottom: "1px solid #435B6D7A",
            }}
          >
            {isLoading &&
              Array.from({
                length: 5,
              })?.map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  sx={{
                    height: "140px",
                    minWidth: "420px",
                    borderRadius: "4px",
                  }}
                />
              ))}
            {!isLoading &&
              sortedExams &&
              sortedExams.map((sortedExam) => (
                <SelectAnalysisCard
                  key={sortedExam.id}
                  exam={sortedExam}
                  selectedExams={selectedExams}
                  isFetchingExamSection={
                    isFetchingExamSection || isFetchingExternalExam
                  }
                  handleExamSelect={handleExamSelect}
                  handleExternalExamSelect={handleExternalExamSelect}
                />
              ))}
          </Box>
        </Box>
      </DefaultDrawer>
    </LightThemeProvider>
  );
};

export default ProgressionAnalysisSlider;
